// src/features/tasks/CreateTaskPage.tsx

import {useEffect, useState} from "react";
import { useForm } from "react-hook-form";
import {useParams, useNavigate, useSearchParams} from "react-router-dom";
import { Card } from "@components/shadcn-ui/card";
import { Button } from "@components/shadcn-ui/button";
import { Input } from "@components/shadcn-ui/input";
import { Textarea } from "@components/shadcn-ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@components/shadcn-ui/select";
import { TaskType } from "../types";
import { createTask } from "../services/tasksService";
import { useToast } from "@customHooks/useToast";
import { Combobox } from "@components/shadcn-ui/combobox";
import { getUsers } from "../services/tasksService";
import {getResourcePath} from "@lib/utils";

export const CreateTaskPage = () => {
    const { resourceId } = useParams<{ resourceId: string }>();
    const [ searchParams ] = useSearchParams();
    const resourceType = searchParams.get("resourceType");
    const navigate = useNavigate();
    const { toast } = useToast();
    const { register, handleSubmit, setValue, watch } = useForm();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [users, setUsers] = useState([]);
    const [isLoadingUsers, setIsLoadingUsers] = useState(true);

    const onSubmit = async (data: any) => {
        setIsSubmitting(true);
        try {
            const userDetails = JSON.parse(localStorage.getItem("userDetails") || "{}");
            const taskData = {
                ...data,
                resourceId: resourceId,
                resourceType: resourceType,
                author: userDetails.userName,
                dueDateInUTCEpoch: new Date(data.dueDate).getTime(),
                watchers: data.watchers == '' || data.watchers == undefined ? [] : data.watchers.split(',').map((w: string) => w.trim()),
                labels: data.labels == '' || data.watchers == undefined ? [] : data.labels.split(',').map((l: string) => l.trim()),
            };
            const createdTask = await createTask(taskData);
            toast({ description: "Task created successfully!" });
            navigate(`${getResourcePath(createdTask)}/${resourceId}/tasks/${createdTask.taskId}`);
        } catch (error) {
            console.error("Error creating task:", error);
            toast({ variant: "destructive", description: "Failed to create task" });
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const fetchedUsers = await getUsers();
                setUsers(fetchedUsers);
            } catch (error) {
                console.error("Error fetching users:", error);
                toast({
                    variant: "destructive",
                    description: "Failed to fetch users"
                });
            } finally {
                setIsLoadingUsers(false);
            }
        };

        fetchUsers();
    }, []);

    return (
        <Card className="p-4">
            <h1 className="text-2xl font-bold mb-4">Create Task</h1>
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="space-y-4">
                    {/*<Input placeholder="Assignee" {...register("assignee", { required: true })} />*/}
                    {isLoadingUsers ? (
                        <div>Loading users...</div>
                    ) : (
                        <Combobox
                            options={users}
                            value={watch("assignee")}
                            onValueChange={(value) => setValue("assignee", value)}
                            placeholder="Select assignee"
                        />
                    )}
                    <Select onValueChange={(value) => setValue("type", value)}>
                        <SelectTrigger>
                            <SelectValue placeholder="Task Type" />
                        </SelectTrigger>
                        <SelectContent>
                            {Object.values(TaskType).map((type) => (
                                <SelectItem key={type} value={type}>{type}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Input type="date" {...register("dueDate", { required: true })} />
                    <Input placeholder="Title" {...register("title", { required: true })} />
                    <Textarea placeholder="Description" {...register("description", { required: true })} />
                    {/*<Input placeholder="Watchers (comma-separated)" {...register("watchers")} />*/}
                    {/*<Select onValueChange={(value) => setValue("priority", value)}>
                        <SelectTrigger>
                            <SelectValue placeholder="Priority" />
                        </SelectTrigger>
                        <SelectContent>
                            {[1, 2, 3, 4, 5].map((priority) => (
                                <SelectItem key={priority} value={priority.toString()}>{priority}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>*/}
                    {/*<Input placeholder="Labels (comma-separated)" {...register("labels")} />*/}
                </div>
                <Button type="submit" className="mt-4" disabled={isSubmitting}>
                    {isSubmitting ? "Creating..." : "Create Task"}
                </Button>
            </form>
        </Card>
    );
};
